import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../hooks/use-user-context';
import { createOptionMap } from '../utilities/misc';
import { rem } from '../utilities/style';

import { ReactComponent as IconSocialFacebook } from '../images/icons/social-facebook.svg';
import { ReactComponent as IconSocialInstagram } from '../images/icons/social-instagram.svg';
import { ReactComponent as IconSocialLinkedin } from '../images/icons/social-linkedin.svg';
import { ReactComponent as IconSocialTwitter } from '../images/icons/social-x.svg';
import { ReactComponent as IconSocialYoutube } from '../images/icons/social-youtube.svg';
import { ReactComponent as IconSocialThreads } from '../images/icons/social-threads.svg';

const Item = styled.div`
  padding: ${rem(6)};
`;

const Container = styled.div`
  display: flex;
  margin: ${rem(-6)};
  align-items: center;

  ${({ size }) => size === 'large' && `
    margin: ${rem(-2)};

    ${Item} {
      padding: ${rem(2)};
    }
  `}
`;

const SocialLink = styled('a', {
  shouldForwardProp: (prop) => !['color', 'hoverColor', 'contained', 'iconColor'].includes(prop)
})`
  transition: color .2s ease, background .2s ease;
  display: block;

  ${({ color, hoverColor, contained, iconColor }) => (contained ? `
    padding: ${rem(4)};
    border-radius: 50%;
    background: ${color};
    color: ${iconColor};

    &:focus,
    &:hover {
      background: ${hoverColor};
    }
  ` : `
    color: ${color};

    &:focus,
    &:hover {
      color: ${hoverColor};
    }
  `)};
`;

const SocialLinks = ({ color, hoverColor, contained, size = 'normal', iconColor = 'white' }) => {
  const data = useStaticQuery(graphql`
    query SocialOptions {
      allContentfulOptionSet(filter: {name: {eq: "social"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const iconSize = size === 'large' ? rem(35) : rem(24);

  return (
    <Container size={size}>
      <Item>
        <SocialLink
          href={options['social.linkedin_url']}
          aria-label={options['social.linkedin_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialLinkedin
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
      <Item>
        <SocialLink
          href={options['social.instagram_url']}
          aria-label={options['social.instagram_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialInstagram
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
      <Item>
        <SocialLink
          href={options['social.threads_url']}
          aria-label={options['social.threads_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialThreads
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
      <Item>
        <SocialLink
          href={options['social.facebook_url']}
          aria-label={options['social.facebook_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialFacebook
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
      <Item>
        <SocialLink
          href={options['social.twitter_url']}
          aria-label={options['social.twitter_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialTwitter
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
      <Item>
        <SocialLink
          href={options['social.youtube_url']}
          aria-label={options['social.youtube_label']}
          target="_blank"
          rel="noopener noreferrer"
          color={color}
          hoverColor={hoverColor}
          contained={contained}
          iconColor={iconColor}
        >
          <IconSocialYoutube
            role="presentation"
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
          />
        </SocialLink>
      </Item>
    </Container>
  );
};

SocialLinks.propTypes = {
  color: PropTypes.string.isRequired,
  hoverColor: PropTypes.string.isRequired,
  contained: PropTypes.bool
};

export default SocialLinks;
